//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import IntegrationPreferencesService from '../../../utils/IntegrationPreferencesService';
import {
  AccessibleIntegrationOption,
  announceDefaultIntegrationChanged,
} from '../../../types/integrationPreferences';
import MemberAccessLink from './MemberAccessLink';

export interface DefaultIntegrationSelectionModalProps {
  open: boolean;
  teamspaceId: string;
  tenantBaseUrl: string;
  getUserContext: () => any;
  /** Called once a default has been saved, so the host can proceed. */
  onSelected: (option: AccessibleIntegrationOption) => void;
  /** Called when the user dismisses without choosing. */
  onDismiss?: () => void;
}

const EVENT_SOURCE = 'selection-modal';

/** Tells other open views (Member Access, Loop Genie's gate) that the default changed. */
const announceDefaultChanged = (): void =>
  announceDefaultIntegrationChanged(EVENT_SOURCE);

const optionKey = (o: AccessibleIntegrationOption): string =>
  `${o.groupId}::${o.integrationName}`;

/** A path whose integration is switched off grants nothing and the server rejects it. */
const isSelectable = (o: AccessibleIntegrationOption): boolean =>
  o.isEnabled !== false;

/**
 * Asks the user which integration AI features should use, the first time that question is
 * genuinely ambiguous.
 *
 * Only shown when two or more paths are reachable and no valid choice is saved; the host
 * decides that from `requiresSelection`. A user with a single path is never asked, which is
 * what keeps the existing single-group experience unchanged.
 */
const DefaultIntegrationSelectionModal: React.FC<
  DefaultIntegrationSelectionModalProps
> = ({
  open,
  teamspaceId,
  tenantBaseUrl,
  getUserContext,
  onSelected,
  onDismiss,
}) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<AccessibleIntegrationOption[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    // The component stays mounted between opens, so a choice made last time would
    // otherwise survive into an option list that may no longer contain it.
    setSelectedKey(null);
    try {
      const service = IntegrationPreferencesService.getInstance(
        tenantBaseUrl,
        getUserContext,
      );
      const prefs = await service.getPreferences(teamspaceId);
      setOptions(prefs.options);

      // If access narrowed to a single path while the modal was opening, there is nothing
      // to ask: save it and let the caller continue rather than showing a one-item choice.
      // Counted over selectable paths only, matching the server's requiresSelection — one
      // usable path alongside a disabled one is not a choice.
      const selectable = prefs.options.filter(isSelectable);
      if (selectable.length === 1) {
        const only = selectable[0];
        await service.setDefaultIntegration(teamspaceId, {
          integrationName: only.integrationName,
          selectedGroupId: only.groupId,
        });
        announceDefaultChanged();
        onSelected(only);
      }
    } catch (e: any) {
      setError(e?.message || t('userGroups.preferencesLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [teamspaceId, tenantBaseUrl, getUserContext, onSelected, t]);

  useEffect(() => {
    if (open) {
      load();
    }
  }, [open, load]);

  const chosen = options.find((o) => optionKey(o) === selectedKey);

  const handleConfirm = async () => {
    if (!chosen || !isSelectable(chosen)) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const service = IntegrationPreferencesService.getInstance(
        tenantBaseUrl,
        getUserContext,
      );
      await service.setDefaultIntegration(teamspaceId, {
        integrationName: chosen.integrationName,
        selectedGroupId: chosen.groupId,
      });
      announceDefaultChanged();
      onSelected(chosen);
    } catch (e: any) {
      setError(e?.message || t('userGroups.preferencesSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next: boolean) => !next && onDismiss?.()}
    >
      <DialogContent data-testid="default-integration-modal">
        <DialogClose
          aria-label={t('userGroups.close', 'Close')}
          data-testid="modal-close"
          className="absolute right-4 top-4 text-lg leading-none opacity-70 hover:opacity-100"
        >
          ×
        </DialogClose>
        {/* DialogTitle, not a bare h2: Radix wires it to aria-labelledby, so without it the
            dialog is announced unnamed. */}
        <DialogTitle className="mb-1 text-base font-semibold">
          {t('userGroups.chooseDefaultIntegration') ||
            'Choose your AI integration'}
        </DialogTitle>
        <p className="mb-4 text-sm opacity-80">
          {t('userGroups.chooseDefaultIntegrationHelp') ||
            'You have access to more than one integration. Choose which one AI features should use.'}
        </p>

        <p className="mb-4 text-xs opacity-70" data-testid="override-hint">
          {t(
            'userGroups.overrideHint',
            'Want to use your own credentials for an integration? Add an override on the',
          )}{' '}
          <MemberAccessLink onNavigate={onDismiss}>
            {t('userGroups.memberAccessPage', 'Member Access page')}
          </MemberAccessLink>
          .
        </p>

        {loading && (
          <div data-testid="modal-loading">
            {t('userGroups.loading') || 'Loading…'}
          </div>
        )}

        {error && (
          <div data-testid="modal-error" role="alert" className="mb-3 text-sm">
            {error}
          </div>
        )}

        {!loading && (
          <ul className="mb-4 flex flex-col gap-2">
            {options.map((option) => {
              const key = optionKey(option);
              const selectable = isSelectable(option);
              return (
                <li key={key}>
                  <label
                    className={`flex items-center gap-3 rounded border p-3 ${
                      selectable
                        ? 'cursor-pointer'
                        : 'cursor-not-allowed opacity-60'
                    }`}
                    data-testid={`modal-option-${key}`}
                  >
                    <input
                      type="radio"
                      name="default-integration"
                      value={key}
                      checked={selectedKey === key}
                      disabled={!selectable}
                      onChange={() => setSelectedKey(key)}
                    />
                    <span>
                      <span className="block text-sm font-medium">
                        {option.integrationName}
                      </span>
                      <span className="block text-xs opacity-75">
                        {option.sourceLabel || `via ${option.groupName}`}
                        {/* Listed rather than hidden: a group the user belongs to
                            vanishing from the list reads as a bug. */}
                        {!selectable &&
                          ` — ${t(
                            'userGroups.integrationUnavailable',
                            'Integration disabled',
                          )}`}
                      </span>
                      {selectable && (
                        <span
                          className="mt-0.5 block text-xs opacity-75"
                          data-testid={`modal-credentials-${key}`}
                        >
                          {option.hasUserOverride
                            ? t(
                                'userGroups.usingOverride',
                                'Using your override',
                              )
                            : t(
                                'userGroups.usingGroupCredentials',
                                'Group credentials',
                              )}
                        </span>
                      )}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onDismiss}
            data-testid="modal-cancel"
          >
            {t('userGroups.cancel', 'Cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={!chosen || !isSelectable(chosen) || saving}
            data-testid="modal-confirm"
          >
            {saving
              ? t('userGroups.saving') || 'Saving…'
              : t('userGroups.confirm') || 'Confirm'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DefaultIntegrationSelectionModal;
